import { z } from 'zod';
import { zfd, json as zfdJsonParse } from 'zod-form-data';
import { registrationContext } from '~/app/features/registrations/registrationMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import { logError } from '~/utils/observability';
import { suggestedRegistrationToDraft } from '~/utils/registration';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import {
  checkUsPostalCode,
  customerInfoSchema,
  draftRegistrationSchema,
  mediaAssetSchema,
} from '~/utils/schemas';

export const { loader, useLoaderData } = makeLoader(({ context }) => {
  const settings = context.get(settingsContext);
  const { suggested, draft } = context.get(registrationContext);

  return {
    ...settings.registrations,
    draft: suggestedRegistrationToDraft(
      draft,
      suggested,
      settings.registrations.customFields,
    ),
    suggestedRegistrationMessage: suggested?.message,
    requirePhone:
      settings.registrations.shouldCollectAddress &&
      !!settings.theme.isRegistrationPhoneRequired,
    uploadFileInfo: {
      storefrontId: settings.storefrontId,
      destination: 'crewProofOfPurchase' as const,
    },
    emailMarketingConsentText: settings.theme.emailMarketingConsentText,
    emailMarketingConsentDescriptionText:
      settings.theme.emailMarketingConsentDescriptionText,
    emailMarketingConsentPolicyUrlTextTitle:
      settings.theme.emailMarketingConsentPolicyUrlTextTitle,
    countryCodes: settings.theme.registrationCountryCodes?.length
      ? settings.theme.registrationCountryCodes
      : undefined,
  };
});

export const FormKeys = {
  StoreSalesChannel: 'storeSalesChannel',
  EstimatedPurchaseDate: 'estimatedPurchaseDate',
  Assets: 'assets',
  RequiredAssetCount: 'requiredAssetCount',
  OrderNumber: 'orderNumber',
  EmailMarketingConsent: 'emailMarketingConsent',
} as const;

export const { action, useActionResult } = makeAction(
  async ({ formData, context }, appStore) => {
    const settings = context.get(settingsContext);
    const registration = context.get(registrationContext);
    const { registrations: registrationSettings, theme } = settings;
    const requirePhone =
      !!theme.isRegistrationPhoneRequired &&
      registrationSettings.shouldCollectAddress;

    const baseSchema = customerInfoSchema
      .extend({
        address: customerInfoSchema.shape.address.required({
          phone: requirePhone || undefined,
        }),
        [FormKeys.StoreSalesChannel]: zfd.numeric(),
        [FormKeys.EstimatedPurchaseDate]: zfd.text(z.string()),
        [FormKeys.OrderNumber]: zfd.text(z.string().optional()),
        [FormKeys.RequiredAssetCount]: zfd.numeric(
          z.number().int().nonnegative(),
        ),
        [FormKeys.Assets]: zfd.repeatableOfType(zfdJsonParse(mediaAssetSchema)),
        [FormKeys.EmailMarketingConsent]: zfd.checkbox().optional(),
      })
      .partial({
        address: !registrationSettings.shouldCollectAddress || undefined,
      })
      // for custom fields
      .catchall(draftRegistrationSchema.shape.customFields.valueType)
      .check(checkUsPostalCode);

    // we don't need the formId in our action as it will be caught with the custom fields in catchAll
    formData.delete('formId');
    const results = zfd.formData(baseSchema).safeParse(formData);

    if (!results.success) {
      const formInfo = Object.fromEntries(formData.entries()) as Record<
        string,
        unknown
      >;

      logError(
        new Error('Malformed form data', {
          cause: {
            error: results.error,
            formInfo,
          },
        }),
      );

      return {
        ok: false,
        message:
          'There was an issue with the data submitted. Please check your information and try again.',
      };
    }

    const {
      email,
      firstName,
      lastName,
      [FormKeys.StoreSalesChannel]: storeSalesChannelId,
      [FormKeys.EstimatedPurchaseDate]: estimatedPurchaseDate,
      [FormKeys.OrderNumber]: orderNumber,
      [FormKeys.RequiredAssetCount]: requiredAssetCount,
      [FormKeys.Assets]: assets = [],
      [FormKeys.EmailMarketingConsent]: emailMarketingConsent,
      address,
      ...customFields
    } = results.data;

    const storeSalesChannel = registrationSettings.storeSalesChannels.find(
      (channel) => channel.id === storeSalesChannelId,
    );

    if (!storeSalesChannel) {
      throw new Error('Invalid store sales channel');
    }

    const assetsRequired = requiredAssetCount > 0;

    if (assetsRequired && assets.length < requiredAssetCount) {
      return {
        ok: false,
        message: `A minimum of ${requiredAssetCount} files are required`,
      };
    }

    appStore.dispatch(
      appStore.actions.registrations.setDraft({
        email,
        firstName,
        lastName,
        storeSalesChannel,
        estimatedPurchaseDate,
        emailMarketingConsentCollectedOn: emailMarketingConsent
          ? new Date().toISOString()
          : undefined,
        address,
        customFields,
        proofOfPurchaseAssets: assets,
        orderNumber,
      }),
    );

    const extractedProductData =
      registration.suggested?.metadata?.extractedProductData ?? [];

    const productMatchMetadata =
      extractedProductData.length > 0
        ? await api.analyzeRegistrationReceiptProduct({
            params: { storefrontId: settings.storefrontId },
            body: {
              storeSalesChannelName: storeSalesChannel.name,
              estimatedPurchaseDate,
              extractedProductData,
            },
          })
        : undefined;

    if (productMatchMetadata) {
      appStore.dispatch(
        appStore.actions.registrations.setSuggested({
          ...registration.suggested,
          productMatchMetadata,
        }),
      );
    }

    return redirect('../product');
  },
);
